<template>
  <el-dialog
    :model-value="modelValue"
    title="Create App"
    width="480px"
    @update:model-value="$emit('update:modelValue', $event)"
    @open="resetForm"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <el-form-item label="Title" prop="title">
        <el-input v-model="form.title" placeholder="Enter app title" />
      </el-form-item>
      <el-form-item label="Bundle" prop="bundle">
        <el-input v-model="form.bundle" placeholder="com.example.app" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">Cancel</el-button>
      <el-button type="primary" :loading="loading" @click="handleSubmit">
        Create
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, defineProps, defineEmits } from "vue";
import { ElMessageBox } from "element-plus";
import type { FormInstance, FormRules } from "element-plus";

defineProps<{
  modelValue: boolean;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "submit", payload: { title: string; bundle: string }): void;
}>();

const formRef = ref<FormInstance>();

const form = reactive({
  title: "",
  bundle: "",
});

const rules: FormRules = {
  title: [
    {
      required: true,
      message: "Title is required",
      trigger: ["blur", "change"],
    },
    {
      min: 2,
      message: "Title must be at least 2 characters",
      trigger: ["blur", "change"],
    },
  ],
  bundle: [
    {
      required: true,
      message: "Bundle is required",
      trigger: ["blur", "change"],
    },
    {
      pattern: /^[a-zA-Z][a-zA-Z0-9]*(\.[a-zA-Z][a-zA-Z0-9]*)+$/,
      message: "Bundle must be a valid identifier (e.g. com.example.app)",
      trigger: ["blur", "change"],
    },
  ],
};

const resetForm = () => {
  form.title = "";
  form.bundle = "";
  formRef.value?.resetFields();
};

const handleSubmit = async () => {
  if (!formRef.value) return;
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) return;

  await ElMessageBox.confirm(
    `App <b>${form.title}</b> with bundle <b>${form.bundle}</b> will be created. This action is irreversible.`,
    "Confirm creation",
    {
      confirmButtonText: "Create",
      cancelButtonText: "Cancel",
      dangerouslyUseHTMLString: true,
      type: "warning",
    }
  ).then(() => {
    emit("submit", { title: form.title, bundle: form.bundle });
  });
};
</script>
